import { useMemo, useState } from "react";
import {
    ArrowUp,
    Building2,
    GraduationCap,
    HeartPulse,
    Home,
    Landmark,
    ShoppingBag,
    Sparkles,
    Wand2
} from "lucide-react";

const quickStarts = [
    {
        key: "sports-academy",
        label: "Sports Academy",
        industry: "Education",
        subIndustry: "Sports Academy",
        prompt: "Build a sports academy website for students and parents with programs, coaches, admissions, training outcomes, and contact enquiries.",
        icon: GraduationCap
    },
    {
        key: "real-estate",
        label: "Real Estate",
        industry: "Real Estate",
        subIndustry: "Real Estate Company",
        prompt: "Build a real estate website for property buyers with listings, services, local expertise, consultation CTA, and lead capture.",
        icon: Home
    },
    {
        key: "clinic",
        label: "Clinic",
        industry: "Healthcare",
        subIndustry: "Clinic",
        prompt: "Build a clinic website with services, doctors, appointments, trust signals, patient-friendly sections, and contact form.",
        icon: HeartPulse
    },
    {
        key: "store",
        label: "Online Store",
        industry: "Ecommerce / Store",
        subIndustry: "Fashion Store",
        prompt: "Build an ecommerce website with products, collections, offers, trust sections, and shopping-focused layout.",
        icon: ShoppingBag
    },
    {
        key: "law-firm",
        label: "Law Firm",
        industry: "Legal",
        subIndustry: "Law Firm",
        prompt: "Build a law firm website with practice areas, team, consultation CTA, credibility, and authority-focused sections.",
        icon: Landmark
    },
    {
        key: "saas",
        label: "SaaS",
        industry: "SaaS / Tech",
        subIndustry: "SaaS Product",
        prompt: "Build a SaaS website with features, pricing, conversion-focused sections, onboarding CTA, and product benefits.",
        icon: Building2
    }
];

function inferIndustryFromPrompt(prompt) {
    const value = String(prompt || "").toLowerCase();

    if (value.includes("academy") || value.includes("school") || value.includes("coaching") || value.includes("sports")) {
        return {
            industry: "Education",
            subIndustry: "Sports Academy",
            goalOfWebsite: "Generate Leads",
            primaryAudience: "Parents / students"
        };
    }

    if (value.includes("real estate") || value.includes("property") || value.includes("listing")) {
        return {
            industry: "Real Estate",
            subIndustry: "Real Estate Company",
            goalOfWebsite: "Generate Leads",
            primaryAudience: "Home buyers"
        };
    }

    if (value.includes("clinic") || value.includes("doctor") || value.includes("health") || value.includes("hospital")) {
        return {
            industry: "Healthcare",
            subIndustry: "Clinic",
            goalOfWebsite: "Book Appointments",
            primaryAudience: "Patients"
        };
    }

    if (value.includes("store") || value.includes("shop") || value.includes("ecommerce") || value.includes("product")) {
        return {
            industry: "Ecommerce / Store",
            subIndustry: "Online Store",
            goalOfWebsite: "Sell Products",
            primaryAudience: "Online shoppers"
        };
    }

    if (value.includes("law") || value.includes("legal") || value.includes("firm") || value.includes("advocate")) {
        return {
            industry: "Legal",
            subIndustry: "Law Firm",
            goalOfWebsite: "Book Consultation",
            primaryAudience: "Business owners"
        };
    }

    if (value.includes("saas") || value.includes("software") || value.includes("app") || value.includes("tech")) {
        return {
            industry: "SaaS / Tech",
            subIndustry: "SaaS Product",
            goalOfWebsite: "Start Free Trial",
            primaryAudience: "Startups / teams"
        };
    }

    if (value.includes("restaurant") || value.includes("cafe") || value.includes("food") || value.includes("menu")) {
        return {
            industry: "Restaurant",
            subIndustry: "Restaurant / Cafe",
            goalOfWebsite: "Get Orders",
            primaryAudience: "Local customers"
        };
    }

    if (value.includes("gym") || value.includes("fitness") || value.includes("trainer") || value.includes("workout")) {
        return {
            industry: "Fitness",
            subIndustry: "Fitness Studio",
            goalOfWebsite: "Book Trial",
            primaryAudience: "Fitness seekers"
        };
    }

    if (value.includes("salon") || value.includes("beauty") || value.includes("spa") || value.includes("makeup")) {
        return {
            industry: "Beauty",
            subIndustry: "Salon / Beauty Studio",
            goalOfWebsite: "Book Appointments",
            primaryAudience: "Local customers"
        };
    }

    if (value.includes("construction") || value.includes("builder") || value.includes("contractor") || value.includes("interior")) {
        return {
            industry: "Construction",
            subIndustry: "Construction Company",
            goalOfWebsite: "Generate Leads",
            primaryAudience: "Property owners"
        };
    }

    return {
        industry: "",
        subIndustry: "",
        goalOfWebsite: "Generate Leads",
        primaryAudience: "Local customers"
    };
}

function buildBusinessNameFromPrompt(prompt) {
    const clean = String(prompt || "").trim();

    if (!clean) return "";

    const match = clean.match(/(?:for|called|named)\s+([a-zA-Z0-9 '&.-]{3,40})/i);

    if (match?.[1]) {
        return match[1].replace(/[.,]$/, "").trim();
    }

    return "";
}

export default function MobileGeneratorComposer({
    form,
    updateField,
    onStart
}) {
    const [prompt, setPrompt] = useState("");

    const canSubmit = prompt.trim().length >= 8;

    const selectedPreview = useMemo(() => {
        const inferred = inferIndustryFromPrompt(prompt);
        return inferred.industry || "AI will detect your industry";
    }, [prompt]);

    const applyPrompt = (inputPrompt) => {
        const finalPrompt = String(inputPrompt || prompt || "").trim();
        if (!finalPrompt) return;

        const inferred = inferIndustryFromPrompt(finalPrompt);
        const guessedName = buildBusinessNameFromPrompt(finalPrompt);

        if (!form.businessName && guessedName) {
            updateField("businessName", guessedName);
        }

        if (inferred.industry) updateField("industry", inferred.industry);
        if (inferred.subIndustry) updateField("subIndustry", inferred.subIndustry);

        updateField("description", finalPrompt);
        updateField("goalOfWebsite", inferred.goalOfWebsite || form.goalOfWebsite || "Generate Leads");
        updateField("primaryAudience", inferred.primaryAudience || form.primaryAudience || "Local customers");
        updateField("brandStyle", form.brandStyle || "Premium");
        updateField("tone", form.tone || "Professional");

        if (onStart) onStart();
    };

    const applyQuickStart = (item) => {
        setPrompt(item.prompt);

        updateField("industry", item.industry);
        updateField("subIndustry", item.subIndustry);
        updateField("description", item.prompt);
        updateField("goalOfWebsite", "Generate Leads");
        updateField("primaryAudience", item.key === "sports-academy" ? "Parents / students" : "Local customers");
        updateField("brandStyle", "Premium");
        updateField("tone", "Professional");

        if (onStart) onStart();
    };

    return (
        <section className="mgc-shell">
            <div className="mgc-topbar">
                <div className="mgc-brand-mark">
                    <Sparkles size={19} />
                </div>

                <div className="mgc-brand-copy">
                    <strong>Shriansh Nexus</strong>
                    <span>AI Website + CRM Builder</span>
                </div>

                <div className="mgc-status-pill">Beta</div>
            </div>

            <div className="mgc-hero">
                <div className="mgc-kicker">
                    <Wand2 size={14} />
                    <span>Launch from one prompt</span>
                </div>

                <h1>Build websites with AI</h1>

                <p>
                    Tell Nexus what you want to create. We will prepare your website,
                    CRM modules, lead flow, and preview.
                </p>
            </div>

            <div className="mgc-prompt-card">
                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe your business... e.g. Build a sports academy website for football training with programs, coaches, admissions and contact enquiries"
                    rows={5}
                />

                <div className="mgc-prompt-footer">
                    <div className="mgc-detected">
                        <span>Detected</span>
                        <strong>{selectedPreview}</strong>
                    </div>

                    <button
                        type="button"
                        className="mgc-send-btn"
                        disabled={!canSubmit}
                        onClick={() => applyPrompt(prompt)}
                        aria-label="Start AI builder"
                    >
                        <ArrowUp size={20} />
                    </button>
                </div>
            </div>

            <div className="mgc-quick-section">
                <div className="mgc-section-title">
                    <span>Quick starts</span>
                    <strong>Choose a business type</strong>
                </div>

                <div className="mgc-chip-row">
                    {quickStarts.map((item) => {
                        const Icon = item.icon;

                        return (
                            <button
                                type="button"
                                key={item.key}
                                className="mgc-chip"
                                onClick={() => applyQuickStart(item)}
                            >
                                <Icon size={15} />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mgc-proof-row">
                <span>Website</span>
                <span>CRM</span>
                <span>Leads</span>
                <span>Publish</span>
            </div>
        </section>
    );
}